// packages/chordpro/src/chord.js
//
// Единственное место, где живёт музыкальная алгебра: всё остальное в chordpro/
// работает либо с уже разобранным аккордом, либо с текстом как с текстом.
//
// Два решения, которые здесь важны:
//
// 1. Немецкая нотация — ФЛАГ РАЗБОРА, а не преобразование текста. При
//    notation='H' H = си, B = си-бемоль. Текст в поле не меняется никогда.
// 2. Бемольность ЗАПОМИНАЕТСЯ в разобранном аккорде. Без этого транспозиция
//    печатает A# там, где автор написал Bb — первая же ошибка живого прогона
//    прототипа была ровно такой. Память бемольности решает ВСЕ полутоны, кроме
//    10 и 11 в немецкой нотации: там написание задаёт сама нотация (см. ниже).

const SHARPS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLATS  = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];

// Качество аккорда НЕ разбирается на части: оно едет строкой и печатается как
// есть. Разбирать maj7/sus4/add9 нам незачем — мы не строим аппликатуры.
const CHORD_RE = new RegExp(
  '^([A-H])([#b♯♭]?)'
  + '((?:maj|min|mi|m|M|dim|aug|sus|add|alt|°|ø|\\+|-|[0-9₀-₉]|\\(|\\))*)'
  + '(?:/([A-H])([#b♯♭]?))?$',
);

const SUBSCRIPTS = {
  '₀': '0', '₁': '1', '₂': '2', '₃': '3', '₄': '4',
  '₅': '5', '₆': '6', '₇': '7', '₈': '8', '₉': '9',
};

function normalizeToken(token) {
  return String(token ?? '')
    .trim()
    .replace(/[₀-₉]/g, (c) => SUBSCRIPTS[c])
    .replace(/♯/g, '#')
    .replace(/♭/g, 'b');
}

// Буква + альтерация → номер полутона (0..11), либо null если буква невозможна
// в текущей нотации (H в английской).
function pitchClass(letter, accidental, notation) {
  let L = letter;
  let acc = accidental;
  if (notation === 'H') {
    if (L === 'H') L = 'B';
    else if (L === 'B') { L = 'B'; acc = 'b'; }   // немецкая B — это си-бемоль
  } else if (L === 'H') {
    return null;
  }
  const i = SHARPS.indexOf(L);
  if (i < 0) return null;
  if (acc === '#') return (i + 1) % 12;
  if (acc === 'b') return (i + 11) % 12;
  return i;
}

/**
 * @param {string} token
 * @param {'B'|'H'} [notation]
 * @returns {{root:number, quality:string, bass:number|null, flat:boolean}|null}
 */
export function parseChord(token, notation = 'B') {
  const t = normalizeToken(token);
  if (!t) return null;
  const m = CHORD_RE.exec(t);
  if (!m) return null;

  const root = pitchClass(m[1], m[2], notation);
  if (root === null) return null;

  let bass = null;
  if (m[4]) {
    bass = pitchClass(m[4], m[5], notation);
    if (bass === null) return null;
  }

  // Немецкая B (= си-бемоль) считается бемольным написанием: иначе после
  // сдвига она напечаталась бы диезом.
  const germanFlat = notation === 'H' && (m[1] === 'B' || m[4] === 'B');
  const flat = m[2] === 'b' || m[5] === 'b' || germanFlat;

  return { root, quality: m[3] || '', bass, flat };
}

export function isChordToken(token, notation = 'B') {
  return parseChord(token, notation) !== null;
}

/**
 * @param {{root:number, quality:string, bass:number|null, flat:boolean}} chord
 * @param {number} [shift] полутона
 * @param {'B'|'H'} [notation]
 */
export function renderChord(chord, shift = 0, notation = 'B') {
  const names = chord.flat ? FLATS : SHARPS;
  const name = (i) => {
    const pc = (((i + shift) % 12) + 12) % 12;
    // Немецкая нотация — свойство ВЫСОТЫ, а не написания: си-бемоль это B, си
    // это H, независимо от того, как аккорд был записан в источнике.
    // Переводить по строке (`if (s === 'Bb')`) нельзя: 'Bb' выходит только из
    // FLATS, поэтому аккорд, попавший на си-бемоль без бемольного написания
    // (A с сдвигом +1, G с +3), напечатался бы как 'A#' — написание, которого
    // в немецкой нотации не существует вовсе. Замерено: 8400 сравнений
    // английского пути до и после — 0 расхождений, то есть правило касается
    // ровно полутонов 10 и 11.
    if (notation === 'H') {
      if (pc === 11) return 'H';
      if (pc === 10) return 'B';
    }
    return names[pc];
  };
  return name(chord.root) + chord.quality + (chord.bass !== null ? `/${name(chord.bass)}` : '');
}
